import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { ArrowLeftIcon, DocumentArrowUpIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { customerService } from '../../services/customerService'
import toast from 'react-hot-toast'

// ── Statement parser ────────────────────────────────────────────────────────

interface ParsedEntry {
  transaction_date: string
  details: string
  debit: number | null
  credit: number | null
  balance: number
  balance_type: 'Dr' | 'Cr'
}

const MONTH: Record<string, number> = {
  jan: 0, january: 0,
  feb: 1, february: 1, februrary: 1,
  mar: 2, march: 2,
  apr: 3, april: 3,
  may: 4,
  jun: 5, june: 5,
  jul: 6, july: 6,
  aug: 7, august: 7,
  sep: 8, september: 8,
  oct: 9, october: 9,
  nov: 10, november: 10,
  dec: 11, december: 11,
}

const SKIP = [
  /total/i, /^bhavik$/i, /^page \d+/i, /start using/i,
  /help:\s*\+/i, /t&c apply/i, /report generated/i,
  /date\s+details\s+debit/i, /opening balance/i,
  /^\(on \d+/i, /phone number:/i, /statement$/i,
  /^kes/i, /no\. of entries/i, /^\(/, /net balance/i,
]

interface StatementMeta { name?: string; phone?: string }

function extractHeader(text: string): StatementMeta {
  const meta: StatementMeta = {}
  for (const line of text.split('\n').map(l => l.trim())) {
    if (!meta.name) {
      const m = line.match(/^(.+?)\s+Statement$/i)
      if (m) meta.name = m[1].trim()
    }
    if (!meta.phone) {
      const m = line.match(/^Phone\s*Number:\s*(\d+)/i)
      if (m) meta.phone = m[1].trim()
    }
    if (meta.name && meta.phone) break
  }
  return meta
}

function parseStatement(text: string): ParsedEntry[] {
  const lines = text.split('\n').map(l => l.trim()).filter(Boolean)
  let year = new Date().getFullYear()
  let prevBal = 0 // positive = Dr, negative = Cr
  const out: ParsedEntry[] = []

  for (const line of lines) {
    // "22 August 2022 (...)" — full date header, sets year
    const fullHeader = line.match(
      /^(\d{1,2})\s+(january|february|februrary|march|april|may|june|july|august|september|october|november|december)\s+(\d{4})/i
    )
    if (fullHeader) { year = parseInt(fullHeader[3]); continue }

    // "September 2022" — month-year header
    const monthHeader = line.match(
      /^(january|february|februrary|march|april|may|june|july|august|september|october|november|december)\s+(\d{4})$/i
    )
    if (monthHeader) { year = parseInt(monthHeader[2]); continue }

    if (SKIP.some(p => p.test(line))) continue

    // Transaction: "DD Mon rest..."
    const m = line.match(/^(\d{1,2})\s+(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+(.+)/i)
    if (!m) continue

    const day = parseInt(m[1])
    const mon = MONTH[m[2].toLowerCase()]
    if (mon === undefined) continue
    const rest = m[3]

    // Extract all decimal numbers (handles Indian comma format)
    const nums = [...rest.matchAll(/[\d,]+\.\d{2}/g)]
    if (nums.length < 2) continue

    // Balance = last number; Dr/Cr = token immediately after
    const lastMatch = nums[nums.length - 1]
    const afterLast = rest.slice(lastMatch.index! + lastMatch[0].length).trim()
    const bType: 'Dr' | 'Cr' = afterLast === 'Cr' ? 'Cr' : 'Dr'

    const rawBal = parseFloat(lastMatch[0].replace(/,/g, ''))
    const signedBal = bType === 'Cr' ? -rawBal : rawBal

    // Transaction amount = second-to-last number
    const txAmt = parseFloat(nums[nums.length - 2][0].replace(/,/g, ''))

    // Details = text before first number
    const details = rest.slice(0, nums[0].index!).trim() || 'Imported entry'

    let debit: number | null = null
    let credit: number | null = null

    if (signedBal > prevBal) {
      debit = txAmt
    } else if (signedBal < prevBal) {
      credit = txAmt
    } else {
      continue
    }

    out.push({
      transaction_date: new Date(year, mon, day).toISOString().split('T')[0],
      details,
      debit,
      credit,
      balance: rawBal,
      balance_type: bType,
    })

    prevBal = signedBal
  }

  return out
}

const fmt = (n: number) =>
  n.toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

// ── Import dialog ───────────────────────────────────────────────────────────

function ImportDialog({
  onAccept,
  onClose,
}: {
  onAccept: (entries: ParsedEntry[], meta: StatementMeta) => void
  onClose: () => void
}) {
  const [raw, setRaw] = useState('')
  const [parsed, setParsed] = useState<ParsedEntry[] | null>(null)
  const [meta, setMeta] = useState<StatementMeta>({})
  const [error, setError] = useState('')

  const handleParse = () => {
    setError('')
    try {
      const entries = parseStatement(raw)
      if (entries.length === 0) {
        setError('No transactions found. Make sure you pasted the full statement text.')
        return
      }
      setParsed(entries)
      setMeta(extractHeader(raw))
    } catch {
      setError('Failed to parse statement. Check the pasted text.')
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Import Customer Statement</h2>
            <p className="text-sm text-gray-500">
              {parsed
                ? `${parsed.length} transactions found — review before accepting`
                : 'Paste the full statement text from Khatabook or a similar app'}
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {!parsed ? (
            /* Step 1: paste */
            <div className="space-y-4">
              <textarea
                className="w-full h-72 px-3 py-2 border border-gray-300 rounded-lg font-mono text-xs focus:ring-2 focus:ring-primary-500 resize-none"
                placeholder="Paste statement text here…"
                value={raw}
                onChange={e => setRaw(e.target.value)}
              />
              {error && <p className="text-sm text-red-600">{error}</p>}
            </div>
          ) : (
            /* Step 2: review */
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b text-gray-500">
                    <th className="pb-2 pr-4 font-medium">Date</th>
                    <th className="pb-2 pr-4 font-medium">Details</th>
                    <th className="pb-2 pr-4 font-medium text-right">Debit (−)</th>
                    <th className="pb-2 pr-4 font-medium text-right">Credit (+)</th>
                    <th className="pb-2 font-medium text-right">Balance</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {parsed.map((e, i) => (
                    <tr key={i} className={e.debit ? 'bg-red-50' : 'bg-green-50'}>
                      <td className="py-1.5 pr-4 whitespace-nowrap text-gray-700">{e.transaction_date}</td>
                      <td className="py-1.5 pr-4 text-gray-900 max-w-xs truncate">{e.details}</td>
                      <td className="py-1.5 pr-4 text-right text-red-700 font-mono">
                        {e.debit ? fmt(e.debit) : ''}
                      </td>
                      <td className="py-1.5 pr-4 text-right text-green-700 font-mono">
                        {e.credit ? fmt(e.credit) : ''}
                      </td>
                      <td className="py-1.5 text-right font-mono text-gray-700">
                        {fmt(e.balance)} {e.balance_type}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t bg-gray-50 rounded-b-xl">
          <button
            onClick={parsed ? () => setParsed(null) : onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 text-sm"
          >
            {parsed ? 'Back' : 'Cancel'}
          </button>
          {!parsed ? (
            <button
              onClick={handleParse}
              disabled={!raw.trim()}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 text-sm"
            >
              Parse Statement
            </button>
          ) : (
            <button
              onClick={() => onAccept(parsed, meta)}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm"
            >
              Accept {parsed.length} Transactions
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Page ────────────────────────────────────────────────────────────────────

export const CreateCustomerPage = () => {
  const navigate = useNavigate()
  const [formData, setFormData] = useState({
    customer_type: 'individual',
    company_name: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    alt_phone: '',
    address: '',
    city: '',
    postal_code: '',
    country: 'Kenya',
    group_id: '',
    tax_id: '',
    credit_limit: '',
    payment_terms_days: '0',
    notes: '',
  })
  const [showImport, setShowImport] = useState(false)
  const [pendingEntries, setPendingEntries] = useState<ParsedEntry[]>([])

  const { data: groupsData, isLoading: isLoadingGroups } = useQuery({
    queryKey: ['customer-groups'],
    queryFn: () => customerService.getGroups(),
  })

  const createMutation = useMutation({
    mutationFn: (data: any) => customerService.create(data),
    onSuccess: async (response: any) => {
      const customerId = response.data.data.customer_id
      if (pendingEntries.length > 0) {
        try {
          await customerService.importStatement(customerId, pendingEntries)
          toast.success(`Customer created with ${pendingEntries.length} imported transactions`)
        } catch {
          toast.success('Customer created')
          toast.error('Statement import failed — re-import from the customer page')
        }
      } else {
        toast.success('Customer created successfully')
      }
      navigate('/customers')
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || error?.message || 'Failed to create customer'
      toast.error(message)
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.group_id) { toast.error('Please select a customer group'); return }
    if (!formData.phone?.trim()) { toast.error('Phone number is required'); return }
    if (formData.customer_type === 'individual' && !formData.first_name && !formData.last_name) {
      toast.error('Please enter at least a first name or last name'); return
    }
    if (formData.customer_type === 'company' && !formData.company_name) {
      toast.error('Please enter a company name'); return
    }
    createMutation.mutate({
      company_name: formData.company_name || null,
      first_name: formData.first_name || null,
      last_name: formData.last_name || null,
      email: formData.email || null,
      phone: formData.phone.trim(),
      address: formData.address || null,
      city: formData.city || null,
      country: formData.country || 'Kenya',
      tax_number: formData.tax_id || null,
      group_id: Number(formData.group_id),
      credit_limit: formData.credit_limit ? Number(formData.credit_limit) : null,
      notes: formData.notes || null,
    })
  }

  const groups = groupsData?.data?.data || []
  const isCompany = formData.customer_type === 'company'

  return (
    <div className="space-y-6">
      {showImport && (
        <ImportDialog
          onAccept={(entries, meta) => {
            setPendingEntries(entries)
            setFormData(p => {
              const updates: Partial<typeof p> = {}
              if (meta.name) {
                if (!p.company_name) updates.company_name = meta.name
                if (!p.first_name && !p.last_name) {
                  const parts = meta.name.trim().split(/\s+/)
                  updates.last_name = parts.length > 1 ? parts.pop()! : ''
                  updates.first_name = parts.join(' ')
                }
              }
              if (meta.phone && !p.phone) updates.phone = meta.phone
              return { ...p, ...updates }
            })
            setShowImport(false)
          }}
          onClose={() => setShowImport(false)}
        />
      )}

      {/* Header */}
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/customers')} className="p-2 hover:bg-gray-100 rounded-lg">
          <ArrowLeftIcon className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Add New Customer</h1>
          <p className="text-sm text-gray-500">Create a new customer in your database</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Customer Type */}
          <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Customer Type</h2>
            <div className="flex gap-4">
              {['individual', 'company'].map(t => (
                <label key={t} className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="customer_type"
                    value={t}
                    checked={formData.customer_type === t}
                    onChange={e => setFormData(p => ({ ...p, customer_type: e.target.value }))}
                    className="w-4 h-4 text-primary-600"
                  />
                  <span className="text-sm text-gray-700 capitalize">{t}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Basic Information */}
          <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              {isCompany ? 'Company Information' : 'Personal Information'}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {isCompany ? (
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.company_name}
                    onChange={e => setFormData(p => ({ ...p, company_name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    placeholder="e.g., ABC Corporation"
                  />
                </div>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                    <input
                      type="text"
                      value={formData.first_name}
                      onChange={e => setFormData(p => ({ ...p, first_name: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      placeholder="John"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                    <input
                      type="text"
                      value={formData.last_name}
                      onChange={e => setFormData(p => ({ ...p, last_name: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      placeholder="Doe"
                    />
                  </div>
                </>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={e => setFormData(p => ({ ...p, email: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="john@example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                <input
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={e => setFormData(p => ({ ...p, phone: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="+254 700 000 000"
                />
                <p className="text-xs text-gray-500 mt-1">Required field</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Alternative Phone</label>
                <input
                  type="tel"
                  value={formData.alt_phone}
                  onChange={e => setFormData(p => ({ ...p, alt_phone: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="+254 711 111 111"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tax ID / PIN</label>
                <input
                  type="text"
                  value={formData.tax_id}
                  onChange={e => setFormData(p => ({ ...p, tax_id: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="A001234567F"
                />
              </div>
            </div>
          </div>

          {/* Address */}
          <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Address</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Street Address</label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={e => setFormData(p => ({ ...p, address: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="123 Main Street, Building 4"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={e => setFormData(p => ({ ...p, city: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="Nairobi"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Postal Code</label>
                <input
                  type="text"
                  value={formData.postal_code}
                  onChange={e => setFormData(p => ({ ...p, postal_code: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="00100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                <input
                  type="text"
                  value={formData.country}
                  onChange={e => setFormData(p => ({ ...p, country: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="Kenya"
                />
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Notes</h2>
            <textarea
              rows={3}
              value={formData.notes}
              onChange={e => setFormData(p => ({ ...p, notes: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              placeholder="Additional notes about this customer..."
            />
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Import Statement */}
          <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Import History</h2>
            <p className="text-xs text-gray-500 mb-4">
              Paste a statement to load past transactions
            </p>
            <button
              type="button"
              onClick={() => setShowImport(true)}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary-400 hover:bg-primary-50 text-sm text-gray-600 hover:text-primary-700 transition-colors"
            >
              <DocumentArrowUpIcon className="w-5 h-5" />
              Import Customer Statement
            </button>
            {pendingEntries.length > 0 && (
              <div className="mt-3 flex items-center justify-between bg-primary-50 border border-primary-200 rounded-lg px-3 py-2">
                <span className="text-sm text-primary-700 font-medium">
                  {pendingEntries.length} transactions queued
                </span>
                <button
                  type="button"
                  onClick={() => setPendingEntries([])}
                  className="text-xs text-primary-600 hover:text-red-600"
                >
                  Clear
                </button>
              </div>
            )}
          </div>

          {/* Customer Group */}
          <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Customer Group</h2>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Group *</label>
              <select
                required
                value={formData.group_id}
                onChange={e => setFormData(p => ({ ...p, group_id: e.target.value }))}
                disabled={isLoadingGroups}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100"
              >
                <option value="">{isLoadingGroups ? 'Loading groups...' : 'Select Group'}</option>
                {groups.map((group: any) => (
                  <option key={group.group_id} value={group.group_id}>
                    {group.group_name} ({group.price_tier})
                  </option>
                ))}
              </select>
              {groups.length === 0 && !isLoadingGroups && (
                <p className="text-xs text-red-500 mt-1">No groups available. Please create a group first.</p>
              )}
            </div>
          </div>

          {/* Credit Settings */}
          <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Credit Settings</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Credit Limit</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.credit_limit}
                  onChange={e => setFormData(p => ({ ...p, credit_limit: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Terms (Days)</label>
                <input
                  type="number"
                  min="0"
                  value={formData.payment_terms_days}
                  onChange={e => setFormData(p => ({ ...p, payment_terms_days: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => navigate('/customers')}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {createMutation.isPending
                ? 'Creating...'
                : pendingEntries.length > 0
                  ? `Create & Import (${pendingEntries.length})`
                  : 'Create Customer'}
            </button>
          </div>
        </div>
      </form>
    </div>
  )
}
